import assetVideo from "./asset-video";

// --------------------- Config --------------------------------//

const PIXELS_PRECISION_VIDEOS = 100;
const MAX_WIDTH_VIDEOS = 2000;
const MAX_HEIGHT_VIDEOS = 2000;
const videoDebug = false;

//--------------------- Downloaded libs --------------------------------//

let hlsLibraryLoaded = false;
let plyrLibraryLoaded = false;
const downloadedLibs = new Set();

//--------------------- Internal utilities -------------------------------//

const isTrue = (el, attr) => el.getAttribute(attr)?.toLowerCase() === "true";
const warn = (...m) => videoDebug && console.warn(...m);

//--------------------- Lazy video helper -------------------------------//

const lazyVideoHelper = {
  addLazyVideos(baseFind = document) {
    const videoSelectors = [".video-generic", ".video-youtube", ".video-vimeo"];
    const observer = new IntersectionObserver(this.handleIntersection.bind(this));

    videoSelectors.forEach((selector) =>
      baseFind.querySelectorAll(selector).forEach((el, i) => {
        el.id ||= `${el.id || "video"}-${Date.now()}-${i}`;
        if (!isTrue(el, "vdmodal")) {
          observer.observe(el);
        }
      }),
    );
  },

  handleIntersection(entries, observer) {
    entries.forEach(({ isIntersecting, target }) => {
      if (isIntersecting) {
        this.lazyLoad(target);
        observer.unobserve(target);
      }
    });
  },

  async lazyLoad(video) {
    if (video.id.includes("GNPlayer")) {
      await this.setupCustomPlayer(video);
    } else if (isTrue(video, "vdautoplay")) {
      video.src = video.src.replace("&autoplay=0", "&autoplay=1");
    }
  },

  async setupCustomPlayer(video) {
    await this.setPosterURL(video);

    if (isTrue(video, "vdautoplay")) {
      Object.assign(video, { muted: true, autoplay: true, loop: true, playsInline: true });
    }

    assetVideo.setupBestSource(video);

    const dynamicMedia = isTrue(video, "dynamicMedia");
    const canPlayNative = isTrue(video, "canPlayNativeDM");

    if (dynamicMedia && !canPlayNative) {
      await this.customPlayerLoadAndPlay(video);
    } else {
      video.load();
    }
  },

  async customPlayerLoadAndPlay(video) {
    await this.loadPlyrLibs();

    const player = new window.Plyr(video, this.getPlyrConfig(video));
    this.setupPlyrEvents(player);
    await this.setupHlsIfNeeded(video);
  },

  async loadPlyrLibs() {
    if (plyrLibraryLoaded) {
      return;
    }

    await Promise.all([import("https://cdn.jsdelivr.net/npm/plyr@3/+esm").then((m) => (window.Plyr = m.default)), this.downloadExternalLib("https://cdn.jsdelivr.net/npm/plyr@3/dist/plyr.min.css", "css")]);

    plyrLibraryLoaded = true;
  },

  getPlyrConfig(video) {
    const controls = isTrue(video, "vdcontrols") ? ["play-large", "play", "progress", "current-time", "mute", "volume", "settings", "pip", "airplay", "fullscreen"] : ["play-large"];

    return {
      debug: videoDebug,
      autoplay: isTrue(video, "vdautoplay"),
      muted: isTrue(video, "vdmuted"),
      loop: { active: video.loop },
      poster: video.dataset.poster,
      playsinline: true,
      controls,
      hideControls: true,
      settings: ["speed", "loop"],
      clickToPlay: true,
      disableContextMenu: true,
      displayDuration: true,
      invertTime: true,
      autopause: true,
      keyboard: { focused: true, global: false },
      fullscreen: { enabled: true, fallback: true, iosNative: true, container: null },
      storage: { enabled: false, key: "plyr" },
      speed: { selected: 1, options: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] },
      captions: { active: false },
      ads: { enabled: false },
    };
  },

  setupPlyrEvents(player) {
    const playBtn = player.elements.buttons?.play?.[0];
    if (playBtn) {
      playBtn.style.display = player.autoplay ? "none" : "block";
    }

    ["ready", "progress", "playing", "pause"].forEach((ev) => player.on(ev, (e) => warn(`Plyr ${ev}`, e)));

    (window.barceloActivePlayers ||= []).push(player);
  },

  async setupHlsIfNeeded(video) {
    const dynamic = this.isDynamicMediaConfigured(video);
    const srcPath = video.querySelector(".videoDynamicPath")?.src;
    if (!dynamic || !srcPath) {
      return ((video.src = srcPath), video.load());
    }

    if (!hlsLibraryLoaded) {
      await import("https://cdn.jsdelivr.net/npm/hls.js@1/+esm").then((m) => (window.Hls = m.default));
      hlsLibraryLoaded = true;
    }

    const hls = new window.Hls(this.getHlsConfig());
    hls.loadSource(srcPath);
    hls.attachMedia(video);
    if (videoDebug) {
      window.hls = hls;
    }

    hls.on(window.Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal) {
        return warn("HLS warning", data);
      }

      const { NETWORK_ERROR, MEDIA_ERROR } = window.Hls.ErrorTypes;
      if (data.type === NETWORK_ERROR) {
        hls.startLoad();
      } else if (data.type === MEDIA_ERROR) {
        hls.recoverMediaError();
      } else {
        hls.destroy();
      }
    });
  },

  isDynamicMediaConfigured(video) {
    return isTrue(video, "dynamicmedia") && (video.getAttribute("videodesktoppathdm") !== "DM-not-configured" || video.getAttribute("videomobilepathdm") !== "DM-not-configured");
  },

  getHlsConfig() {
    return {
      debug: videoDebug,
      autoStartLoad: true,
      startPosition: -1,
      startLevel: -1,
      capLevelToPlayerSize: true,

      // Buffer
      maxBufferLength: 8,
      maxMaxBufferLength: 20,
      maxBufferHole: 0.5,
      backBufferLength: 10,

      // Performance
      enableWorker: true,
      enableSoftwareAES: false,
      maxFragLookUpTolerance: 0.2,
      testBandwidth: true,

      // Low-latency and progressive
      lowLatencyMode: false,
      progressive: false,
      startFragPrefetch: true,

      // Subtitles
      enableWebVTT: false,
      enableLowInitialPlaylist: true,

      // Timeouts and retries
      fragLoadingTimeOut: 15000,
      fragLoadingMaxRetry: 4,
      fragLoadingRetryDelay: 1000,
      fragLoadingMaxRetryTimeout: 30000,
    };
  },

  modalCheckEnableVideo(el) {
    el.id.includes("GNPlayer") ? this.setupCustomPlayer(el) : (el.src = el.src.replace("&autoplay=0", "&autoplay=1"));
    this.watchModalVideo(el);
  },

  modalCheckDisableVideo(el) {
    if (el.id.includes("GNPlayer")) {
      this.disableCustomPlayer(el);
    } else {
      el.src = el.src.replace("&autoplay=1", "&autoplay=0");
    }
  },

  watchModalVideo(el) {
    const modal = el.closest(".tingle-modal, .brh-video__modal");
    if (!modal) {
      return;
    }

    const mo = new MutationObserver((m) => {
      if (m.some(({ attributeName }) => attributeName === "class")) {
        this.modalCheckDisableVideo(el);
        mo.disconnect();
      }
    });
    mo.observe(modal, { attributes: true });
  },

  async setPosterURL(video) {
    const fallback = video.dataset.posterpreview;
    if (!fallback) {
      return;
    }

    try {
      const meta = await fetch(`${fallback}/jcr:content/metadata.0.json`, { headers: { Accept: "application/json" } }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.statusText))));

      video.dataset.poster = this.constructPosterURL(meta, video);
    } catch (err) {
      warn("Poster fallback", err.message);
      video.dataset.poster = fallback;
    }
    video.poster = video.dataset.poster;
  },

  constructPosterURL(data, video) {
    const file = data["dam:scene7File"];
    if (!file) {
      return video.dataset.posterpreview;
    }

    const base = `${data["dam:scene7Domain"]}is/image/${file}`;
    const w = this.getOptimalWidth(video);
    const h = this.getOptimalHeight(video);

    return `${base}?wid=${w}&hei=${h}&fit=crop,1&qlt=45&fmt=avif-alpha&cache=update,on,on`;
  },

  getOptimalWidth(v) {
    return Math.min(v.clientWidth + PIXELS_PRECISION_VIDEOS, MAX_WIDTH_VIDEOS);
  },
  getOptimalHeight(v) {
    return Math.min(v.clientHeight + PIXELS_PRECISION_VIDEOS, MAX_HEIGHT_VIDEOS);
  },

  disableCustomPlayer(el) {
    const list = window.barceloActivePlayers;
    if (!list?.length) {
      return;
    }
    const idx = list.findIndex((p) => p.elements?.original === el);
    if (idx > -1) {
      list.splice(idx, 1)[0].destroy();
    }
  },

  async downloadExternalLib(url, type) {
    if (downloadedLibs.has(url)) {
      return;
    }

    await new Promise((res, rej) => {
      if (type === "css") {
        const preload = Object.assign(document.createElement("link"), {
          rel: "preload",
          href: url,
          as: "style",
          onload() {
            document.head.appendChild(Object.assign(document.createElement("link"), { rel: "stylesheet", href: url }));
            res();
          },
          onerror: rej,
        });
        return document.head.appendChild(preload);
      }

      if (type === "js") {
        const s = Object.assign(document.createElement("script"), {
          src: url,
          defer: true,
          fetchpriority: "high",
          onload: res,
          onerror: rej,
        });
        return document.head.appendChild(s);
      }

      rej(new TypeError(`Tipo de recurso no soportado: ${type}`));
    });

    downloadedLibs.add(url);
  },
};

export default lazyVideoHelper;
